import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { X, Award, User } from "lucide-react";
import { offerScholarship } from "../../services/scholarships";
import Input from "../common/Input";

interface ScholarshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: any;
  onSuccess?: () => void;
}

const ScholarshipModal: React.FC<ScholarshipModalProps> = ({
  isOpen,
  onClose,
  student,
  onSuccess,
}) => {
  const [percentage, setPercentage] = useState<number>(0);
  const [reason, setReason] = useState<string>("");

  const offerScholarshipMutation = useMutation({
    mutationFn: offerScholarship,
    onSuccess: () => {
      toast.success("Scholarship offered successfully");
      onClose();
      onSuccess?.();
      // Reset form
      setPercentage(0);
      setReason("");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to offer scholarship"
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (percentage <= 0 || percentage > 100) {
      toast.error("Percentage must be between 1 and 100");
      return;
    }

    offerScholarshipMutation.mutate({
      studentId: student._id,
      percentage,
      reason: reason.trim() || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-xl p-2">
                <Award className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Offer Scholarship
                </h2>
                <p className="text-purple-100 text-sm font-medium">
                  Provide financial assistance to student
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Student Info */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-3">
                <User className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">
                  {student?.userInfo?.firstName} {student?.userInfo?.lastName}
                </h3>
                <p className="text-sm text-slate-600 font-medium">
                  Student ID: {student?.studentId}
                </p>
                <p className="text-sm text-slate-600 font-medium">
                  Admission: {student?.admissionNumber}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Scholarship Percentage *
              </label>
              <Input
                type="number"
                value={percentage}
                onChange={(e) => setPercentage(Number(e.target.value))}
                placeholder="Enter percentage (1-100)"
                min={1}
                max={100}
                required
                className="w-full"
              />
              <p className="text-xs text-slate-500 mt-1">
                This will reduce the student's fee by the specified percentage
              </p>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for scholarship..."
                className="w-full px-4 py-3 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all resize-none"
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50"
                disabled={offerScholarshipMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50"
                disabled={offerScholarshipMutation.isPending}
              >
                {offerScholarshipMutation.isPending
                  ? "Offering..."
                  : "Offer Scholarship"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScholarshipModal;
